<script setup lang="ts">
import type { ClinicDetailResponse } from '@/types'
import { vetbotUrl } from '~~/shared/utils/vetbot'

const vetbotClaimHref = vetbotUrl('claim-incentive')

const props = defineProps<{
  clinic: ClinicDetailResponse
}>()

const emit = defineEmits<{
  claimed: []
}>()

const r2PublicUrl = useRuntimeConfig().public.r2PublicUrl

// Emergency status for smart phone routing
// Only consider emergency hours when isEmergency is enabled
const { isEmergencyActive } = useEmergencyStatus(
  computed(() => props.clinic.workingHours),
  computed(() => props.clinic.isEmergency ? props.clinic.emergencyHours : null),
  computed(() => props.clinic.is24Hours),
)

const activePhone = computed(() => {
  if (isEmergencyActive.value && props.clinic.emergencyPhone) {
    return props.clinic.emergencyPhone
  }
  return props.clinic.phone || props.clinic.emergencyPhone || null
})

const isShowingEmergencyPhone = computed(() => {
  if (isEmergencyActive.value && props.clinic.emergencyPhone) return true
  if (!props.clinic.phone && props.clinic.emergencyPhone) return true
  return false
})

const hasCoordinates = computed(() => {
  return props.clinic.latitude !== null && props.clinic.longitude !== null
})

const directionsUrl = computed(() => {
  if (!hasCoordinates.value) return null
  return `https://www.google.com/maps/dir/?api=1&destination=${props.clinic.latitude},${props.clinic.longitude}`
})

const formattedEmergencyFee = computed(() => {
  if (props.clinic.emergencyFeeMin === null || props.clinic.emergencyFeeMax === null) return null
  if (props.clinic.emergencyFeeMin === props.clinic.emergencyFeeMax) {
    return `${props.clinic.emergencyFeeMin} Kč`
  }
  return `${props.clinic.emergencyFeeMin}–${props.clinic.emergencyFeeMax} Kč`
})

const formattedHolidayHours = computed(() => {
  if (!props.clinic.holidayHours) return null
  const hours = props.clinic.holidayHours
  if (hours.status === 'closed') return 'Zavřeno'
  if (hours.status === '24h') return 'Nonstop'
  return hours.intervals.map((i) => `${i.open}-${i.close}`).join(', ')
})

const formattedEmergencyHolidayHours = computed(() => {
  if (!props.clinic.emergencyHolidayHours) return null
  const hours = props.clinic.emergencyHolidayHours
  if (hours.status === 'closed') return 'Zavřeno'
  if (hours.status === '24h') return 'Nonstop'
  return hours.intervals.map((i) => `${i.open}-${i.close}`).join(', ')
})

function getImageUrl(key: string) {
  return `${r2PublicUrl}/${key}`
}

// Analytics tracking
const { trackPhoneClick, trackWebsiteClick, trackDirectionsClick } = useClinicAnalytics()
const { capture } = useAnalytics()

const onPhoneClick = () => {
  trackPhoneClick(props.clinic.id)
  capture('clinic_phone_clicked', { clinic_id: props.clinic.id, clinic_name: props.clinic.name, is_premium: props.clinic.isPremium, is_emergency_phone: isShowingEmergencyPhone.value })
}
const onWebsiteClick = () => {
  trackWebsiteClick(props.clinic.id)
  capture('clinic_website_clicked', { clinic_id: props.clinic.id, clinic_name: props.clinic.name, is_premium: props.clinic.isPremium })
}
const onDirectionsClick = () => {
  trackDirectionsClick(props.clinic.id)
  capture('clinic_directions_clicked', { clinic_id: props.clinic.id, clinic_name: props.clinic.name, is_premium: props.clinic.isPremium })
}
const onEmailClick = () => {
  capture('email_clicked', { clinic_id: props.clinic.id, clinic_name: props.clinic.name, is_premium: props.clinic.isPremium })
}

// About section expand/collapse
const isAboutExpanded = ref(false)
const aboutRef = ref<HTMLElement | null>(null)
const isAboutClamped = ref(false)

onMounted(() => {
  nextTick(() => {
    if (aboutRef.value) {
      isAboutClamped.value = aboutRef.value.scrollHeight > aboutRef.value.clientHeight
    }
  })
})

// Lightbox
const isLightboxOpen = ref(false)
const lightboxIndex = ref(0)

function openLightbox(index: number) {
  lightboxIndex.value = index
  isLightboxOpen.value = true
}

// Claim modal
const isClaimModalOpen = ref(false)
const canShowClaimButton = computed(() => !props.clinic.isVerified)

function onClaimSuccess() {
  emit('claimed')
}

// Admin actions
const { isAdmin } = useUser()
const adminDeleteModalOpen = ref(false)
const adminActionLoading = ref(false)
const toast = useToast()

async function handleAdminDelete() {
  adminActionLoading.value = true
  try {
    await $fetch(`/api/admin/clinics/${props.clinic.slug}` as string, { method: 'DELETE' })
    toast.add({ title: 'Klinika smazána', color: 'success' })
    adminDeleteModalOpen.value = false
    navigateTo('/admin/kliniky')
  } catch (e: unknown) {
    const err = e as { data?: { message?: string } }
    toast.add({ title: err.data?.message || 'Chyba', color: 'error' })
  } finally {
    adminActionLoading.value = false
  }
}
</script>

<template>
  <!-- Admin action bar -->
  <ClientOnly>
    <div v-if="isAdmin" class="flex items-center gap-2 mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
      <UIcon name="i-lucide-shield" class="size-4 text-blue-600" />
      <span class="text-sm font-medium text-blue-700">Admin</span>
      <div class="flex-1" />
      <UButton
        :to="`/admin/kliniky/${clinic.slug}`"
        external
        color="primary"
        variant="soft"
        size="xs"
        icon="i-lucide-pencil"
      >
        Upravit
      </UButton>
      <UButton
        color="error"
        variant="soft"
        size="xs"
        icon="i-lucide-trash-2"
        @click="adminDeleteModalOpen = true"
      >
        Smazat
      </UButton>
    </div>
  </ClientOnly>

  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <!-- Main Content (Left Column) -->
    <div class="lg:col-span-2 space-y-6">
      <UCard>
        <div class="space-y-6">
          <!-- Header -->
          <div class="flex gap-4">
            <img
              v-if="clinic.isPremium && clinic.images?.length"
              :src="getImageUrl(clinic.images[0]!)"
              :alt="clinic.name"
              loading="eager"
              class="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-cover shrink-0 cursor-pointer"
              @click="openLightbox(0)"
            >
            <div class="space-y-2">
              <div class="flex items-center gap-2 flex-wrap">
                <EmergencyBadge
                  v-if="clinic.isEmergency"
                  :is24h="clinic.is24Hours"
                  :is-on-call-only="clinic.isOnCallOnly"
                  size="lg"
                />
                <PremiumBadge v-if="clinic.isVerified" variant="verified" size="lg" />
                <PremiumBadge v-if="clinic.isPremium" variant="premium" size="lg" />
              </div>
              <h1 class="text-2xl md:text-3xl font-bold text-highlighted">
                {{ clinic.name }}
              </h1>
              <a
                v-if="clinic.rating && clinic.reviewCount > 0"
                href="#recenze"
                class="flex items-center gap-2 text-sm text-muted hover:text-primary transition-colors"
              >
                <RatingStars :rating="clinic.rating" :show-value="true" size="md" />
                <span>{{ clinic.reviewCount }} hodnocení</span>
              </a>
            </div>
          </div>

          <USeparator />

          <!-- Contact Info Grid -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <!-- Address -->
            <div v-if="clinic.fullAddress" class="flex items-start gap-3">
              <div class="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <UIcon name="i-lucide-map-pin" class="w-5 h-5 text-muted" />
              </div>
              <div>
                <div class="text-sm text-dimmed mb-0.5">Adresa</div>
                <a
                  v-if="directionsUrl"
                  :href="directionsUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-highlighted font-medium hover:text-primary transition-colors"
                  @click="onDirectionsClick"
                >
                  {{ clinic.fullAddress }}
                </a>
                <div v-else class="text-highlighted font-medium">{{ clinic.fullAddress }}</div>
              </div>
            </div>

            <!-- Phone -->
            <div v-if="clinic.phone || clinic.emergencyPhone" class="flex items-start gap-3">
              <div class="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <UIcon name="i-lucide-phone" class="w-5 h-5 text-muted" />
              </div>
              <div>
                <ClientOnly>
                  <template v-if="isShowingEmergencyPhone">
                    <div class="text-sm text-amber-600 mb-0.5">Pohotovostní telefon</div>
                    <a
                      :href="`tel:${activePhone!.replace(/\s/g, '')}`"
                      class="block text-highlighted font-medium hover:text-primary transition-colors"
                      @click="onPhoneClick"
                    >
                      {{ activePhone }}
                    </a>
                    <p v-if="isEmergencyActive && clinic.phone" class="text-xs text-dimmed mt-1">
                      Ordinační tel.: {{ clinic.phone }}
                    </p>
                  </template>
                  <template v-else>
                    <div class="text-sm text-dimmed mb-0.5">Telefon</div>
                    <a
                      :href="`tel:${activePhone!.replace(/\s/g, '')}`"
                      class="block text-highlighted font-medium hover:text-primary transition-colors"
                      @click="onPhoneClick"
                    >
                      {{ activePhone }}
                    </a>
                  </template>
                  <!-- SSR fallback -->
                  <template #fallback>
                    <div class="text-sm text-dimmed mb-0.5">{{ clinic.phone ? 'Telefon' : 'Pohotovostní telefon' }}</div>
                    <a
                      :href="`tel:${(clinic.phone || clinic.emergencyPhone || '').replace(/\s/g, '')}`"
                      class="block text-highlighted font-medium hover:text-primary transition-colors"
                      @click="onPhoneClick"
                    >
                      {{ clinic.phone || clinic.emergencyPhone }}
                    </a>
                  </template>
                </ClientOnly>
              </div>
            </div>

            <!-- Email (client-only to prevent bot scraping) -->
            <ClientOnly>
              <div v-if="clinic.email" class="flex items-start gap-3">
                <div class="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  <UIcon name="i-lucide-mail" class="w-5 h-5 text-muted" />
                </div>
                <div class="min-w-0">
                  <div class="text-sm text-dimmed mb-0.5">Email</div>
                  <a
                    :href="`mailto:${clinic.email}`"
                    class="text-primary hover:underline break-all"
                    @click="onEmailClick"
                  >
                    {{ clinic.email }}
                  </a>
                </div>
              </div>
            </ClientOnly>

            <!-- Website -->
            <div v-if="clinic.website" class="flex items-start gap-3">
              <div class="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <UIcon name="i-lucide-globe" class="w-5 h-5 text-muted" />
              </div>
              <div class="min-w-0">
                <div class="text-sm text-dimmed mb-0.5">Web</div>
                <a
                  :href="clinic.website"
                  target="_blank"
                  :rel="clinic.isVerified ? 'noopener noreferrer' : 'nofollow noopener noreferrer'"
                  class="text-primary hover:underline break-all"
                  @click="onWebsiteClick"
                >
                  {{ clinic.website.replace(/^https?:\/\//, '').replace(/\/$/, '') }}
                </a>
              </div>
            </div>

          </div>


          <!-- Claim CTA Banner -->
          <div v-if="canShowClaimButton" class="rounded-xl border-2 border-solid border-primary/20 bg-primary/5 p-5 sm:p-6">
              <div class="flex flex-col sm:flex-row sm:items-center gap-4">
                <div class="flex-1 space-y-1">
                  <div class="flex items-center gap-2">
                    <UIcon name="i-lucide-building-2" class="w-5 h-5 text-primary" />
                    <h3 class="text-lg font-semibold text-highlighted">Vlastníte tuto kliniku?</h3>
                  </div>
                  <p class="text-muted">
                    Převezměte profil své kliniky a získejte plnou kontrolu — aktualizujte informace, přidejte fotky, odpovídejte na recenze a oslovte nové klienty.
                  </p>
                  <p class="text-sm text-muted">
                    Bonus: po převzetí profilu získáte 10% slevu na
                    <a
                      :href="vetbotClaimHref"
                      target="_blank"
                      rel="noopener"
                      class="text-primary hover:underline"
                    >Vetbot</a>
                    — AI recepční pro vaši ordinaci.
                  </p>
                </div>
                <ClaimModal
                  v-model:open="isClaimModalOpen"
                  :clinic-slug="clinic.slug"
                  :clinic-name="clinic.name"
                  @success="onClaimSuccess"
                >
                  <UButton
                    color="primary"
                    size="lg"
                    icon="i-lucide-badge-check"
                    class="w-full sm:w-auto whitespace-nowrap"
                  >
                    Převzít kliniku
                  </UButton>
                </ClaimModal>
              </div>
          </div>

          <!-- About -->
          <div v-if="clinic.about">
            <h2 class="text-lg font-semibold text-highlighted mb-4">
              O klinice
            </h2>
            <div
              ref="aboutRef"
              class="prose text-muted whitespace-pre-line"
              :class="{ 'line-clamp-6': !isAboutExpanded }"
            >
              {{ clinic.about }}
            </div>
            <button
              v-if="isAboutClamped"
              class="mt-2 text-sm text-primary hover:underline cursor-pointer"
              @click="isAboutExpanded = !isAboutExpanded"
            >
              {{ isAboutExpanded ? 'Zobrazit méně' : 'Zobrazit více' }}
            </button>
          </div>

          <!-- Photo Gallery (Premium only) -->
          <div v-if="clinic.images && clinic.images.length > (clinic.isPremium ? 1 : 0)">
            <h2 class="text-lg font-semibold text-highlighted mb-4">
              Fotogalerie
            </h2>
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              <button
                v-for="(key, index) in clinic.isPremium ? clinic.images.slice(1) : clinic.images"
                :key="key"
                class="aspect-[4/3] rounded-lg overflow-hidden bg-muted cursor-pointer"
                @click="openLightbox(clinic.isPremium ? index + 1 : index)"
              >
                <img
                  :src="getImageUrl(key)"
                  :alt="`Fotografie kliniky ${clinic.name} ${index + 1}`"
                  class="w-full h-full object-cover"
                  loading="lazy"
                >
              </button>
            </div>
          </div>

          <!-- Specializations -->
          <div v-if="clinic.specializations && clinic.specializations.length > 0">
            <h2 class="text-lg font-semibold text-highlighted mb-4">Specializace</h2>
            <div class="flex flex-wrap gap-2">
              <UBadge
                v-for="spec in clinic.specializations"
                :key="spec"
                color="primary"
                variant="subtle"
                size="md"
              >
                {{ spec }}
              </UBadge>
            </div>
          </div>

          <!-- Services -->
          <div v-if="clinic.services && clinic.services.length > 0">
            <h2 class="text-lg font-semibold text-highlighted mb-4">Služby</h2>
            <div class="flex flex-wrap gap-2">
              <UBadge
                v-for="svc in clinic.services"
                :key="svc"
                color="success"
                variant="subtle"
                size="md"
              >
                {{ svc }}
              </UBadge>
            </div>
          </div>

          <!-- Certifications -->
          <div v-if="clinic.certifications && clinic.certifications.length > 0">
            <h2 class="text-lg font-semibold text-highlighted mb-4">Certifikace</h2>
            <div class="flex flex-wrap gap-2">
              <UBadge
                v-for="cert in clinic.certifications"
                :key="cert"
                color="info"
                variant="subtle"
                size="md"
              >
                {{ cert }}
              </UBadge>
            </div>
          </div>

          <!-- Team Members -->
          <div v-if="clinic.teamMembers && clinic.teamMembers.length > 0">
            <h2 class="text-lg font-semibold text-highlighted mb-4">Náš tým</h2>
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              <div
                v-for="member in clinic.teamMembers"
                :key="member.name"
                class="flex flex-col items-center text-center gap-2"
              >
                <div class="size-20 rounded-lg overflow-hidden bg-muted border border-default">
                  <img
                    v-if="member.image"
                    :src="getImageUrl(member.image)"
                    :alt="member.name"
                    class="w-full h-full object-cover"
                    loading="lazy"
                  >
                  <div v-else class="w-full h-full flex items-center justify-center">
                    <UIcon name="i-lucide-user" class="size-8 text-dimmed" />
                  </div>
                </div>
                <div>
                  <div class="text-sm font-medium text-highlighted">{{ member.name }}</div>
                  <div class="text-xs text-dimmed">{{ member.role }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </UCard>
    </div>

    <!-- Sidebar (Right Column) -->
    <div class="space-y-6 lg:sticky lg:top-24 lg:self-start">
      <!-- Quick Actions (Desktop) -->
      <div class="hidden md:flex flex-col gap-3">
        <ClientOnly>
          <UButton
            v-if="activePhone"
            :to="`tel:${activePhone.replace(/\s/g, '')}`"
            :color="isShowingEmergencyPhone ? 'warning' : 'primary'"
            size="xl"
            :icon="isShowingEmergencyPhone ? 'i-lucide-siren' : 'i-lucide-phone'"
            class="w-full"
            @click="onPhoneClick"
          >
            <div class="text-left">
              <div>{{ isShowingEmergencyPhone ? 'Zavolat (pohotovost)' : 'Zavolat' }}</div>
              <div class="text-xs font-normal opacity-80">{{ activePhone }}</div>
            </div>
          </UButton>
          <template #fallback>
            <UButton
              v-if="clinic.phone || clinic.emergencyPhone"
              :to="`tel:${(clinic.phone || clinic.emergencyPhone || '').replace(/\s/g, '')}`"
              :color="!clinic.phone ? 'warning' : 'primary'"
              size="xl"
              :icon="!clinic.phone ? 'i-lucide-siren' : 'i-lucide-phone'"
              class="w-full"
              @click="onPhoneClick"
            >
              <div class="text-left">
                <div>{{ !clinic.phone ? 'Zavolat (pohotovost)' : 'Zavolat' }}</div>
                <div class="text-xs font-normal opacity-80">{{ clinic.phone || clinic.emergencyPhone }}</div>
              </div>
            </UButton>
          </template>
        </ClientOnly>
        <UButton
          v-if="directionsUrl"
          :to="directionsUrl"
          target="_blank"
          color="neutral"
          variant="outline"
          size="xl"
          icon="i-lucide-navigation"
          class="w-full"
          @click="onDirectionsClick"
        >
          Navigovat
        </UButton>
        <UButton
          v-if="clinic.website"
          :to="clinic.website"
          target="_blank"
          :rel="clinic.isVerified ? 'noopener noreferrer' : 'nofollow noopener noreferrer'"
          color="neutral"
          variant="ghost"
          size="sm"
          icon="i-lucide-external-link"
          class="w-full"
          @click="onWebsiteClick"
        >
          Navštívit web
        </UButton>
      </div>

      <!-- Opening Hours Card -->
      <UCard v-if="clinic.workingHours || clinic.is24Hours">
        <template #header>
          <div class="space-y-1">
            <div class="flex items-center gap-2">
              <UIcon name="i-lucide-clock" class="w-4 h-4 text-muted" />
              <span class="font-semibold text-highlighted">Otevírací doba</span>
            </div>
            <OpenStatus
              :hours="clinic.workingHours"
              :is24-hours="clinic.is24Hours"
              :emergency-hours="clinic.isEmergency ? clinic.emergencyHours : null"
              :is-on-call-only="clinic.isOnCallOnly"
            />
          </div>
        </template>
        <ClinicHours
          v-if="clinic.workingHours"
          :hours="clinic.workingHours"
          :emergency-hours="clinic.isEmergency ? clinic.emergencyHours : null"
          :emergency-fee="clinic.isEmergency ? formattedEmergencyFee : null"
          :holiday-hours="formattedHolidayHours"
          :emergency-holiday-hours="clinic.isEmergency ? formattedEmergencyHolidayHours : null"
        />
        <p v-if="!clinic.isEmergency" class="text-sm text-dimmed mt-4">
          Tato klinika nenabízí pohotovostní služby. Před návštěvou doporučujeme ověřit dostupnost telefonicky.
        </p>
      </UCard>

      <!-- Map Card -->
      <UCard v-if="hasCoordinates">
        <template #header>
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-map" class="w-4 h-4 text-muted" />
            <span class="font-semibold text-highlighted">Poloha</span>
          </div>
        </template>
        <ClinicMap
          :latitude="clinic.latitude!"
          :longitude="clinic.longitude!"
          :name="clinic.name"
        />
      </UCard>

    </div>
  </div>

  <!-- Lightbox -->
  <ImageLightbox
    v-if="isLightboxOpen && clinic.images?.length"
    :images="clinic.images"
    :initial-index="lightboxIndex"
    :clinic-name="clinic.name"
    @close="isLightboxOpen = false"
  />

  <!-- Admin delete modal -->
  <ClientOnly>
    <UModal v-if="isAdmin" v-model:open="adminDeleteModalOpen">
      <template #content>
        <div class="p-6 space-y-4">
          <h3 class="text-lg font-semibold text-highlighted">Smazat kliniku?</h3>
          <p class="text-muted">Klinika bude skryta z veřejných výpisů. Později ji můžete obnovit v administraci.</p>
          <div class="flex justify-end gap-2">
            <UButton color="neutral" variant="ghost" @click="adminDeleteModalOpen = false">Zrušit</UButton>
            <UButton color="error" :loading="adminActionLoading" @click="handleAdminDelete">Smazat</UButton>
          </div>
        </div>
      </template>
    </UModal>
  </ClientOnly>
</template>
